import React, { useEffect, useState } from 'react';
import { announcementAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const PRIORITY_CFG = {
  HIGH:   { bg: '#fee2e2', color: '#991b1b', dot: '#ef4444', border: '#fca5a5' },
  NORMAL: { bg: '#dbeafe', color: '#1d4ed8', dot: '#3b82f6', border: '#93c5fd' },
  LOW:    { bg: '#ede9fe', color: '#6d28d9', dot: '#8b5cf6', border: '#c4b5fd' },
};
const emptyForm = { title: '', content: '', priority: 'NORMAL' };

export default function Announcements() {
  const { isAdmin } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = () => {
    setLoading(true);
    const req = isAdmin() ? announcementAPI.getAll() : announcementAPI.getActive();
    req.then(r => { setAnnouncements(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  };

  const openAdd = () => { setEditing(null); setForm(emptyForm); setModal(true); };
  const openEdit = a => { setEditing(a); setForm({ title: a.title, content: a.content, priority: a.priority || 'NORMAL' }); setModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) { await announcementAPI.update(editing.id, form); toast.success('Updated!'); }
      else { await announcementAPI.create(form); toast.success('Published!'); }
      setModal(false); load();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm('Deactivate this announcement?')) return;
    try { await announcementAPI.deactivate(id); toast.success('Deactivated!'); load(); }
    catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete permanently?')) return;
    try { await announcementAPI.delete(id); toast.success('Deleted!'); load(); }
    catch { toast.error('Failed'); }
  };

  const active = announcements.filter(a => a.active !== false).length;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Announcements</div>
          <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>
            {isAdmin() ? `${active} active · ${announcements.length - active} inactive` : `${active} active announcements`}
          </div>
        </div>
        {isAdmin() && (
          <button onClick={openAdd} style={{ padding: '10px 22px', background: 'linear-gradient(135deg, #1a237e, #1565c0)', color: 'white', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13, boxShadow: '0 4px 14px rgba(26,35,126,0.3)' }}>
            + New Announcement
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 80, color: '#94a3b8' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>⏳</div>Loading...
        </div>
      ) : announcements.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 80, background: '#fff', borderRadius: 16, color: '#94a3b8', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
          <div style={{ fontWeight: 700 }}>No announcements yet</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {announcements.map(ann => {
            const cfg = PRIORITY_CFG[ann.priority] || PRIORITY_CFG.NORMAL;
            const isActive = ann.active !== false;
            return (
              <div key={ann.id} style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', borderLeft: `4px solid ${isActive ? cfg.dot : '#e2e8f0'}`, opacity: isActive ? 1 : 0.6, transition: 'box-shadow 0.15s' }}>
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: isActive ? cfg.bg : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>📢</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>{ann.title}</span>
                      <span style={{ background: cfg.bg, color: cfg.color, padding: '3px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, border: `1px solid ${cfg.border}` }}>{ann.priority || 'NORMAL'}</span>
                      {!isActive && <span style={{ background: '#f1f5f9', color: '#94a3b8', padding: '3px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>INACTIVE</span>}
                    </div>
                    <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, marginBottom: 8 }}>{ann.content}</div>
                    {ann.createdAt && (
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        {new Date(ann.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        {ann.createdBy && ` · by ${ann.createdBy}`}
                      </div>
                    )}
                  </div>
                  {isAdmin() && (
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button onClick={() => openEdit(ann)} style={{ padding: '6px 14px', borderRadius: 8, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Edit</button>
                      {isActive && <button onClick={() => handleDeactivate(ann.id)} style={{ padding: '6px 12px', borderRadius: 8, background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Hide</button>}
                      <button onClick={() => handleDelete(ann.id)} style={{ padding: '6px 12px', borderRadius: 8, background: '#fff1f2', color: '#e11d48', border: '1px solid #fecdd3', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>Del</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
          <div style={{ background: '#fff', borderRadius: 20, width: '90%', maxWidth: 520, boxShadow: '0 32px 80px rgba(0,0,0,0.35)', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #0a1628, #1a237e)', padding: '22px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ color: '#fff', fontWeight: 800, fontSize: 18 }}>{editing ? 'Edit Announcement' : 'New Announcement'}</div>
              <button onClick={() => setModal(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <form onSubmit={handleSubmit} style={{ padding: 28 }}>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, letterSpacing: 0.5, textTransform: 'uppercase' }}>Title *</div>
                <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required placeholder="Announcement title"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, letterSpacing: 0.5, textTransform: 'uppercase' }}>Content *</div>
                <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} required rows={4} placeholder="Write your announcement..."
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 10, letterSpacing: 0.5, textTransform: 'uppercase' }}>Priority</div>
                <div style={{ display: 'flex', gap: 10 }}>
                  {Object.entries(PRIORITY_CFG).map(([p, cfg]) => (
                    <button type="button" key={p} onClick={() => setForm({ ...form, priority: p })} style={{
                      flex: 1, padding: '9px', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                      background: form.priority === p ? cfg.dot : cfg.bg,
                      color: form.priority === p ? '#fff' : cfg.color,
                      border: `2px solid ${form.priority === p ? cfg.dot : 'transparent'}`,
                      transition: 'all 0.15s',
                    }}>{p}</button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={() => setModal(false)} style={{ flex: 1, padding: '11px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ flex: 2, padding: '11px', borderRadius: 10, background: 'linear-gradient(135deg, #1a237e, #1565c0)', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, opacity: saving ? 0.7 : 1, boxShadow: '0 4px 14px rgba(26,35,126,0.3)' }}>
                  {saving ? 'Saving...' : editing ? 'Update' : 'Publish'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
